import styled from "@emotion/styled";
import dayjs from 'dayjs';
import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { DateFormat, i18n, openAddressModal, useAppDispatch, useForm } from "src/common";
import { Button, Flex, InputBirthDate, InputCity, InputFullName, InputOIB, InputPhoneNumber, InputStreet, InputText, RadioGroup, Validation } from "src/components";
import { FieldConfig, Form, FormConfig, FormRef } from "./form";

export type ContractorFormItemType = 'personType' | 'birthDate' | 'fullName' | 'companyName' | 'oib' | 'city' | 'street' | 'houseNumber' | 'partnerContactEmail' | 'phoneNumber';
export const allContractorFormItems: Array<ContractorFormItemType> = ['personType', 'birthDate', 'partnerContactEmail', 'phoneNumber', 'fullName', 'companyName', 'oib', 'city', 'street', 'houseNumber'];

const FlexStyle = styled(Flex)({
  width: '100%',
  marginTop: 24
})
interface ContractorFormProps {
  contractor: any;
  formItems?: Array<ContractorFormItemType>;
  onAddressChange?: (value: any) => void;
  storedValues?: any;
}

export const ContractorForm = forwardRef<FormRef, ContractorFormProps>(({ contractor, formItems = allContractorFormItems, onAddressChange, storedValues }, ref) => {
  const { formRef, submitForm, getValues, setFieldValue, validateForm, scrollIntoView } = useForm();
  const [contractorInitialValues, setContractorInitialValues] = React.useState<any | undefined>(undefined);
  const dispatch = useAppDispatch();
  
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      submitForm();
    },
    getValues: () => getValues(),
    setFieldValue: (fieldName: string, value: any, validation?: boolean) => setFieldValue(fieldName, value, validation),
    validateForm: (validateFields?: string[]) => validateForm(validateFields),
    scrollIntoView: () => scrollIntoView()
  }));


  const handleSubmit = (values: { [key: string]: any }) => {
    console.log('Submitted values:', values);
  };

  const modelToForm = (data: any) => {
    const formData: any = {
      isSamePerson: data.isSamePerson,
      personType: data.personType,
    };

    if (data.personType === 'person' && data.person) {
      const { person } = data;

      Object.keys(person).forEach((key) => {
        if (key !== "__typename") {
          const value = (person[key].value === null || person[key].value === "" || person[key].value === 0) && storedValues && storedValues[key] ? storedValues[key] : person[key].value;
          if (value !== null)
            formData[key] = value;
          else
            formData[key] = "";
        }
      });

      formData.birthDate = formData.birthDate ? dayjs(formData.birthDate).format(DateFormat) : '';
    }

    if (data.personType === 'company' && data.company) {
      const { company } = data;

      Object.keys(company).forEach((key) => {
        if (key !== "__typename") {
          const valueCompany = (company[key].value === null || company[key].value === "" || company[key].value === 0) && storedValues && storedValues[key] ? storedValues[key] : company[key].value;
          if (valueCompany !== null)
            formData[key] = valueCompany;
          else
            formData[key] = "";
        }
      });
    }
    formData.fullName = `${formData.firstName ?? ""} ${formData.lastName ?? ""}`.trim();
    return formData;
  }

  useEffect(() => {
    const modelData = modelToForm(contractor);
    const contractorValues = {
      ...modelData,
      // ...storedValues
    };
    setContractorInitialValues(contractorValues);
  } , [contractor]);

  // const contractorInitialValues = modelToForm(contractor);
  // contractorInitialValues.fullName = `${contractorInitialValues.firstName ?? ""} ${contractorInitialValues.lastName ?? ""}`.trim();

  const fields: FieldConfig[] = [
    {
      name: 'personType',
      component: RadioGroup,
      options: [
        { label: i18n.t('components.forms.contractorForm.person'), value: 'person' },
        { label: i18n.t('components.forms.contractorForm.company'), value: 'company' }
      ]
    },
    {
      name: 'fullName',
      label: i18n.t('components.forms.contractorForm.fullName'),
      component: InputFullName,
      showIf: (values: any) => values.personType === 'person',
      disabled: (contractor.person?.firstName?.isEditable === false || contractor.person?.lastName?.isEditable === false),
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange({ name: value.fullName });
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validFullName', message: 'Unesi ispravno ime i prezime' }
      ] as Validation[],
    },
    {
      name: 'companyName',
      label: i18n.t('components.forms.contractorForm.companyName'),
      component: InputText,
      showIf: (values: any) => values.personType === 'company',
      disabled: (contractor.company?.companyName?.isEditable === false),
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange({ name: value });
      },
      validations: [
        { type: 'required', message: 'Potreban unos' }
      ] as Validation[],
    },
    {
      name: 'birthDate',
      label: i18n.t('components.forms.contractorForm.dateOfBirth'),
      component: InputBirthDate,
      showIf: (values: any) => values.personType === 'person',
      disabled: contractor.person?.birthDate?.isEditable === false,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validDate', message: 'Unesi ispravan datum rođenja' },
      ] as Validation[],
    },
    {
      name: 'oib',
      label: i18n.t('components.forms.contractorForm.oib'),
      component: InputOIB,
      disabled: (contractor.person?.oib?.isEditable === false || contractor.company?.oib?.isEditable === false),
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validOIB', message: 'Netočan unos. Unesi ispravni OIB.' },
      ] as Validation[],
    },
    {
      name: 'city',
      label: i18n.t('components.forms.contractorForm.city'),
      component: InputCity,
      disabled: (contractor.person?.city?.isEditable === false || contractor.person?.cityKompareId?.isEditable === false || contractor.company?.city?.isEditable === false || contractor.company?.cityKompareId?.isEditable === false),
      formatValue: (values: any) => (values.city || values.zipCode) && `${values.city}, ${values.zipCode}`,
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange(value);
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'street',
      label: i18n.t('components.forms.contractorForm.street'),
      component: InputStreet,
      disabled: (contractor.person?.street?.isEditable === false || contractor.person?.streetKompareId?.isEditable === false || contractor.company?.street?.isEditable === false || contractor.company?.streetKompareId?.isEditable === false),
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange(value);
      },
      onBlur: () => {
        validateForm(['street']);
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validStreets', message: 'Adresa nije pronađena, pokušaj ponovno.', params: { streetsFound: 'streetsFound' } },
      ] as Validation[],
    },
    {
      name: 'houseNumber',
      label: i18n.t('components.forms.contractorForm.number'),
      component: InputText,
      componentProps: {
        maxLength: 5,
        autoComplete: "new-houseNumber-lvl2"
      },
      disabled: (contractor.person?.houseNumber?.isEditable === false || contractor.company?.houseNumber?.isEditable === false),
      onChange: (value: any) => {
        if (onAddressChange) onAddressChange({ houseNumber: value });
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validHouseNumber', message: 'Netočan unos. Unesi ispravan kućni broj.' },
      ] as Validation[],
      afterElement: (contractor.person?.city?.isEditable === true || contractor.company?.city?.isEditable === true) && <FlexStyle gap={8} align='center'>
        <Button btnType='textPrimary' size='small' onClick={(e) => { e.preventDefault(); dispatch(openAddressModal({ type: 'contractor' })); }}>{i18n.t('components.forms.insuredPersonForm.cantFindAddress')}<b>{i18n.t('components.forms.insuredPersonForm.clickHere')}</b></Button>
      </FlexStyle>
    },
    {
      name: 'partnerContactEmail',
      label: i18n.t('components.forms.contractorForm.email'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      // disabled: (contractor.person?.partnerContactEmail?.isEditable === false || contractor.company?.partnerContactEmail?.isEditable === false),
      validations: [
        { type: 'required', message: 'Polje je obavezno.' },
        { type: 'validMail', message: 'Unesite ispravnu e-mail adresu.' },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: i18n.t('components.forms.contractorForm.phone'),
      component: InputPhoneNumber,
      disabled: (contractor.person?.phoneNumber?.isEditable === false || contractor.company?.phoneNumber?.isEditable === false),
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
      ] as Validation[],
    },
  ].filter((field) => formItems.includes(field.name as ContractorFormItemType));

  const formConfig: FormConfig = {
    gap: 24
  };

  if (!contractorInitialValues) return null;
  return (
    <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={contractorInitialValues} reduxName='contractor' />
  )
});
